import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import AIMemoryBanner from '../components/AIMemoryBanner';
import LedgerTable from '../components/ledger/LedgerTable';
import { LedgerFilters, applyLedgerFilters, EMPTY_LEDGER_FILTERS } from '../components/ledger/LedgerFilters';
import { MonthlyCashflowTable } from '../components/MonthlyCashflowTable';
import SettlementEditor from '../components/ledger/SettlementEditor';
import { OpeningRepairBanner } from '../components/ledger/OpeningRepairBanner';
import { Plus, X, Search, BookOpen, Trash2, Edit2, RefreshCw, TrendingDown, TrendingUp, Package, Users, Wallet, ShoppingCart, Tag, ArrowUpRight, ArrowDownRight, Minus, ArrowLeftRight, BarChart3 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmt2 = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n || 0);

const EMPTY_FORM = {
  name: '', type: 'Creditor', gstin: '', address: '', city: '', state: 'Maharashtra',
  pin_code: '', phone: '', email: '', payment_terms: 'Net 30', credit_limit: '',
  credit_period_days: 30,
  opening_balance: '', opening_balance_type: 'Cr', opening_balance_as_of: '',
  opening_balance_note: '',
  notes: '',
};

export default function Creditors() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  // iter219 — ledger row edit/delete is allowed for admin/owner/manager/accountant
  const canEditLedger = ['admin', 'owner', 'manager', 'accountant'].includes(user?.role);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [ledgerClient, setLedgerClient] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  // iter196 — Tabbed drawer: Ledger / Purchase Orders+Bills / Price History
  const [drawerTab, setDrawerTab] = useState('ledger');
  // iter210 — Role filter for the ledger view (only meaningful when client.type === 'Both')
  const [creditorRoleFilter, setCreditorRoleFilter] = useState('all');
  const [ledgerFilters, setLedgerFilters] = useState(EMPTY_LEDGER_FILTERS);
  const [vendorTxns, setVendorTxns] = useState({ orders: [], bills: [], loaded: false });
  const [priceHistory, setPriceHistory] = useState({ data: null, loading: false });

  // iter162 — Lumpsum payment modal for creditors
  const [showLumpsum, setShowLumpsum] = useState(false);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/clients/`, { withCredentials: true });
      setClients(res.data.filter(c => c.type === 'Creditor' || c.type === 'Both'));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const { deleteState, requestDelete, confirmDelete, cancelDelete } = useAdminDelete(fetchData);

  useEffect(() => { fetchData(); }, []);

  const openAdd = () => { setEditing(null); setForm({ ...EMPTY_FORM, type: 'Creditor' }); setShowModal(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY_FORM, ...c }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      if (editing) {
        const res = await axios.put(`${API}/clients/${editing.id}`, form, { withCredentials: true });
        setClients(prev => prev.map(c => c.id === editing.id ? res.data : c).filter(c => c.type === 'Creditor' || c.type === 'Both'));
      } else {
        const res = await axios.post(`${API}/clients/`, form, { withCredentials: true });
        if (res.data.type === 'Creditor' || res.data.type === 'Both') {
          setClients(prev => [...prev, res.data]);
        }
      }
      setShowModal(false);
      toast.success('Creditor saved!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving creditor'); }
    finally { setSubmitting(false); }
  };

  // deleteClient now uses useAdminDelete hook via requestDelete (see button onClick handlers)

  const viewLedger = async (client) => {
    setLedgerClient(client);
    setLedgerLoading(true);
    setDrawerTab('ledger');
    setVendorTxns({ orders: [], bills: [], loaded: false });
    setPriceHistory({ data: null, loading: false });
    try {
      const res = await axios.get(`${API}/clients/${client.id}/ledger`, { withCredentials: true });
      setLedger(res.data);
    } catch (e) { toast.error('Error loading ledger'); }
    finally { setLedgerLoading(false); }
  };

  // iter196 — Load all POs + bills for this vendor (lazy on tab click)
  const loadVendorTxns = async () => {
    if (vendorTxns.loaded || !ledgerClient) return;
    try {
      const [posRes, billsRes] = await Promise.all([
        axios.get(`${API}/purchases/orders`, { withCredentials: true }),
        axios.get(`${API}/purchases/bills`, { withCredentials: true }),
      ]);
      const myOrders = (posRes.data || []).filter(o => (o.vendor_name || '').toLowerCase() === ledgerClient.name.toLowerCase());
      const myBills  = (billsRes.data || []).filter(b => (b.vendor_name || '').toLowerCase() === ledgerClient.name.toLowerCase());
      setVendorTxns({ orders: myOrders, bills: myBills, loaded: true });
    } catch (e) {
      toast.error('Error loading purchase orders');
    }
  };

  // iter196 — Lazy price-history load on tab click
  const loadPriceHistory = async () => {
    if (priceHistory.data || !ledgerClient) return;
    setPriceHistory({ data: null, loading: true });
    try {
      const res = await axios.get(`${API}/clients/${ledgerClient.id}/price-history`, { withCredentials: true });
      setPriceHistory({ data: res.data, loading: false });
    } catch (e) {
      toast.error('Error loading price history');
      setPriceHistory({ data: null, loading: false });
    }
  };

  useEffect(() => {
    if (drawerTab === 'orders') loadVendorTxns();
    if (drawerTab === 'pricing') loadPriceHistory();
  }, [drawerTab, ledgerClient?.id, loadVendorTxns, loadPriceHistory]);

  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.city?.toLowerCase().includes(search.toLowerCase()) ||
    c.gstin?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="text-zinc-400 text-sm animate-pulse">Loading creditors...</div>;

  return (
    <div className="space-y-5" data-testid="creditors-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Sundry Creditors</h1>
          <p className="text-zinc-500 text-sm mt-0.5">Vendors & suppliers you owe — {clients.length} creditors</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="add-creditor-btn" onClick={openAdd}
            className="flex items-center gap-1.5 bg-purple-600 hover:bg-purple-500 text-white font-semibold text-xs px-3 py-2 rounded-xl transition-colors">
            <Plus size={14} /> New Creditor
          </button>
          {isAdmin && (
            <button data-testid="realloc-fifo-creditors-btn" onClick={async () => {
              if (!window.confirm('Reallocate ALL creditor lumpsum payments strictly against the OLDEST bills first (opening balance → oldest bill → newer)?\n\nSafe & idempotent — already-correct settlements are untouched.')) return;
              try {
                const res = await axios.post(`${API}/purchases/reallocate-fifo`, {}, { withCredentials: true });
                const { total_applied, vendors_touched, report } = res.data || {};
                const touched = vendors_touched ?? (report ? report.length : 0);
                toast.success(`FIFO reallocation done · ${touched} vendor(s) touched · ₹${(total_applied || 0).toLocaleString()} re-applied`);
                await fetchData();
              } catch (e) { toast.error(e.response?.data?.detail || 'Reallocation failed'); }
            }}
              title="Re-run oldest-bill-first settlement across every creditor. Idempotent."
              className="flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-purple-400 font-semibold text-xs px-3 py-2 rounded-xl border border-purple-700/40 transition-colors">
              <ArrowLeftRight size={14} /> Reallocate FIFO
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { label: 'Total Creditors', value: clients.length, icon: Users, color: 'text-purple-400', bg: 'bg-purple-500/10' },
          { label: 'With GSTIN', value: clients.filter(c => c.gstin).length, icon: TrendingDown, color: 'text-amber-400', bg: 'bg-amber-500/10' },
          { label: 'Also Debtors', value: clients.filter(c => c.type === 'Both').length, icon: Package, color: 'text-blue-400', bg: 'bg-blue-500/10' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
              <Icon size={16} className={color} />
            </div>
            <div>
              <div className="text-zinc-400 text-xs">{label}</div>
              <div className="text-zinc-50 font-heading font-bold text-xl">{value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-xs">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          placeholder="Search by name, city, GSTIN..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full bg-zinc-900 border border-zinc-800 rounded-xl pl-9 pr-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-purple-500"
          data-testid="creditor-search"
        />
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="border-b border-zinc-800">
              {['Creditor Name', 'GSTIN', 'City', 'Phone', 'Payment Terms', 'Actions'].map(h => (
                <th key={h} className="text-left text-zinc-500 text-xs font-medium px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors" data-testid={`creditor-row-${c.id}`}>
                <td className="px-4 py-3">
                  <button onClick={() => viewLedger(c)} className="text-purple-400 hover:text-purple-300 font-medium text-xs transition-colors">
                    {c.name}
                  </button>
                  {c.type === 'Both' && <span className="ml-2 text-[10px] bg-blue-900/30 text-blue-400 px-1.5 py-0.5 rounded-full">Also Debtor</span>}
                </td>
                <td className="px-4 py-3 text-zinc-400 text-xs font-mono">{c.gstin || '—'}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{c.city || '—'}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{c.phone || '—'}</td>
                <td className="px-4 py-3 text-zinc-300 text-xs">{c.payment_terms || '—'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => viewLedger(c)} title="View Ledger"
                      className="text-zinc-500 hover:text-purple-400 transition-colors" data-testid={`creditor-ledger-btn-${c.id}`}>
                      <BookOpen size={13} />
                    </button>
                    <button onClick={() => openEdit(c)} title="Modify all fields"
                      data-testid={`modify-creditor-btn-${c.id}`}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg bg-purple-900/30 text-purple-200 hover:bg-purple-800/50 border border-purple-700/40 text-[11px] font-semibold transition-colors">
                      <Edit2 size={11} /> Modify
                    </button>
                    {isAdmin && <button onClick={() => requestDelete(`/clients/${c.id}`, c.name, `Delete creditor "${c.name}" and all linked data.`)} title="Delete"
                      className="p-1 rounded-lg bg-red-900/30 text-red-400 hover:text-red-300 border border-red-800/30">
                      <Trash2 size={13} />
                    </button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center text-zinc-500 text-sm py-10">
            {search ? 'No creditors match your search' : 'No creditors yet — add your first creditor'}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg my-6">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
              <h3 className="font-heading font-semibold text-zinc-50">{editing ? 'Edit Creditor' : 'New Sundry Creditor'}</h3>
              <button onClick={() => setShowModal(false)}><X size={18} className="text-zinc-500 hover:text-zinc-200" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4" data-testid="creditor-form">
              {editing && form.name && <AIMemoryBanner vendor={form.name} />}
              <div>
                <label className="text-zinc-400 text-xs mb-1.5 block">Client Type</label>
                <div className="flex gap-2">
                  {['Creditor', 'Both'].map(t => (
                    <button key={t} type="button"
                      onClick={() => setForm(f => ({ ...f, type: t }))}
                      className={`flex-1 py-2 rounded-xl border text-xs font-semibold transition-all ${form.type === t ? (t === 'Both' ? 'bg-amber-900/40 text-amber-300 border-amber-700' : 'bg-purple-900/40 text-purple-300 border-purple-700') : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'}`}>
                      {t === 'Both' ? 'Debtor + Creditor' : 'Sundry Creditor'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-zinc-400 text-xs mb-1.5 block">Vendor / Supplier Name *</label>
                  <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. ABC Spices Pvt Ltd" data-testid="creditor-name-input"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-purple-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">GSTIN</label>
                  <input value={form.gstin} onChange={e => setForm(f => ({ ...f, gstin: e.target.value }))}
                    placeholder="27AABCX..." className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-purple-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Phone</label>
                  <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="+91 9876543210" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-purple-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Email</label>
                  <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="vendor@supplier.com" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-purple-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">City</label>
                  <input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                    placeholder="Pune" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-purple-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">State</label>
                  <input value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-purple-500" />
                </div>
                <div className="col-span-2">
                  <label className="text-zinc-400 text-xs mb-1.5 block">Address</label>
                  <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                    placeholder="Full address" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-purple-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Payment Terms</label>
                  <select value={form.payment_terms} onChange={e => setForm(f => ({ ...f, payment_terms: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-purple-500">
                    {['Immediate', 'Net 7', 'Net 15', 'Net 30', 'Net 45', 'Net 60', 'Advance'].map(t => (
                      <option key={t} value={t} className="bg-zinc-900">{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1.5 block">Credit Period (days) <span className="text-amber-400">*</span></label>
                  <input type="number" min="0" max="180" value={form.credit_period_days}
                    data-testid="creditor-credit-period-input"
                    onChange={e => setForm(f => ({ ...f, credit_period_days: parseInt(e.target.value || '0', 10) }))}
                    placeholder="30"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-purple-500" />
                  <div className="text-[10px] text-zinc-500 mt-1">Auto-computes due date on every new bill from this vendor.</div>
                </div>
                <div className="col-span-2">
                  <label className="text-zinc-400 text-xs mb-1.5 block">Notes</label>
                  <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Optional notes" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-purple-500" />
                </div>
                {/* ── Opening Balance (FY carry-forward) ────────────────────── */}
                <div className="col-span-2 mt-2 p-3 rounded-xl bg-purple-500/5 border border-purple-500/20">
                  <div className="text-purple-300 text-xs font-semibold mb-2 flex items-center gap-1.5">
                    <BookOpen size={12} /> Opening Balance — Carry forward from previous FY
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <label className="text-zinc-400 text-[10px] mb-1 block">Amount (₹)</label>
                      <input type="number" min="0" step="any" value={form.opening_balance}
                        data-testid="opening-balance-input"
                        onChange={e => setForm(f => ({ ...f, opening_balance: e.target.value }))}
                        placeholder="0"
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-2 text-zinc-50 text-sm focus:outline-none focus:border-purple-500" />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-[10px] mb-1 block">Type</label>
                      <select value={form.opening_balance_type}
                        data-testid="opening-balance-type"
                        onChange={e => setForm(f => ({ ...f, opening_balance_type: e.target.value }))}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-2 text-zinc-50 text-sm focus:outline-none focus:border-purple-500">
                        <option value="Cr" className="bg-zinc-900">Cr (we owe)</option>
                        <option value="Dr" className="bg-zinc-900">Dr (advance paid)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-zinc-400 text-[10px] mb-1 block">As of</label>
                      <input type="date" value={form.opening_balance_as_of}
                        data-testid="opening-balance-date"
                        onChange={e => setForm(f => ({ ...f, opening_balance_as_of: e.target.value }))}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-2 text-zinc-50 text-sm focus:outline-none focus:border-purple-500" />
                    </div>
                  </div>
                  <input value={form.opening_balance_note || ''}
                    onChange={e => setForm(f => ({ ...f, opening_balance_note: e.target.value }))}
                    placeholder="Note (e.g. 'As per audited books FY 2025-26')"
                    className="w-full mt-2 bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-2 text-zinc-300 text-xs focus:outline-none focus:border-purple-500" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-sm transition-colors">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="save-creditor-btn"
                  className="flex-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-semibold text-sm disabled:opacity-50 transition-colors">
                  {submitting ? 'Saving...' : editing ? 'Update Creditor' : 'Add Creditor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ledger Panel */}
      {ledgerClient && (
        <div className="fixed inset-0 bg-black/70 flex items-start justify-end z-50">
          <div className="h-full w-full max-w-3xl bg-zinc-950 border-l border-zinc-800 flex flex-col" data-testid="creditor-ledger-drawer">
            <div className="px-3 sm:px-5 py-3 sm:py-4 border-b border-zinc-800 flex items-center justify-between gap-2 bg-zinc-900">
              <div className="min-w-0">
                <h3 className="font-heading font-semibold text-zinc-50 truncate">{ledgerClient.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs px-2 py-0.5 rounded-full border bg-purple-900/40 text-purple-300 border-purple-700">Creditor</span>
                  {ledgerClient.gstin && <span className="text-zinc-500 text-xs font-mono">{ledgerClient.gstin}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                <button data-testid={`creditor-lumpsum-btn-${ledgerClient.id}`}
                  onClick={() => setShowLumpsum(true)}
                  className="flex items-center gap-1.5 text-xs bg-green-500/20 hover:bg-green-500/30 border border-green-500/40 text-green-200 px-2.5 sm:px-3 py-1.5 rounded-xl transition-colors">
                  <Wallet size={11} /> Pay / Advance
                </button>
                <button data-testid={`modify-creditor-from-ledger-${ledgerClient.id}`}
                  onClick={() => { openEdit(ledgerClient); }}
                  className="flex items-center gap-1.5 text-xs bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/40 text-purple-200 px-2.5 sm:px-3 py-1.5 rounded-xl transition-colors">
                  <Edit2 size={11} /> <span className="hidden sm:inline">Modify Profile</span>
                </button>
                <button onClick={() => { setLedgerClient(null); setLedger(null); }}
                  className="text-zinc-500 hover:text-zinc-200"><X size={18} /></button>
              </div>
            </div>

            {ledger && (
              <>
                {ledger.opening_balance > 0 && (
                  <div className="px-4 pt-4" data-testid="opening-balance-card">
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <BookOpen size={14} className="text-purple-300" />
                        <div>
                          <div className="text-purple-300 text-xs font-semibold">Opening Balance (carried forward)</div>
                          <div className="text-zinc-500 text-[10px] mt-0.5">
                            As of {ledger.opening_balance_as_of || '—'}
                            {ledger.client?.opening_balance_note ? ` · ${ledger.client.opening_balance_note}` : ''}
                          </div>
                        </div>
                      </div>
                      <div className={`font-mono font-bold text-sm ${ledger.opening_balance_type === 'Cr' ? 'text-red-400' : 'text-green-400'}`}>
                        {fmt(ledger.opening_balance)} <span className="text-[10px] opacity-70">{ledger.opening_balance_type}</span>
                      </div>
                    </div>
                  </div>
                )}
                <OpeningRepairBanner ledger={ledger} clientId={ledgerClient.id} canEdit={canEditLedger} onRepaired={() => viewLedger(ledgerClient)} />
                <div className="grid grid-cols-3 gap-2 sm:gap-3 p-3 sm:p-4 border-b border-zinc-800">
                  <div className="bg-zinc-900 rounded-xl p-2 sm:p-3 text-center min-w-0">
                    <div className="text-zinc-500 text-[10px] sm:text-xs">Total Purchased</div>
                    <div className="text-amber-400 font-mono font-bold text-xs sm:text-sm mt-1 truncate">{fmt(ledger.total_purchased ?? ledger.total_invoiced)}</div>
                  </div>
                  <div className="bg-zinc-900 rounded-xl p-2 sm:p-3 text-center min-w-0">
                    <div className="text-zinc-500 text-[10px] sm:text-xs">Total Paid</div>
                    <div className="text-green-400 font-mono font-bold text-xs sm:text-sm mt-1 truncate">{fmt(ledger.total_payments_made ?? ledger.total_paid)}</div>
                  </div>
                  <div className="bg-zinc-900 rounded-xl p-2 sm:p-3 text-center min-w-0">
                    <div className="text-zinc-500 text-[10px] sm:text-xs">Balance ({ledger.balance_type})</div>
                    <div className={`font-mono font-bold text-xs sm:text-sm mt-1 truncate ${ledger.outstanding < 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {fmt(Math.abs(ledger.outstanding))}
                    </div>
                    {/* iter208 — Vendor advance pill. Only renders when we
                        have paid the vendor more than they've billed (Dr
                        balance with a creditor profile). */}
                    {ledger.advance_balance > 0 && (
                      <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40"
                        data-testid="advance-balance-pill"
                        title="On-account advance paid to this vendor. Auto-offers settlement on the next purchase bill.">
                        <span className="font-mono">↑</span> {fmt(ledger.advance_balance)} advance
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* iter196 — Tabs row inside the drawer */}
            <div className="px-2 sm:px-4 pt-3 border-b border-zinc-800 flex items-center gap-1 bg-zinc-950 overflow-x-auto" data-testid="creditor-drawer-tabs">
              {[
                { id: 'ledger',  icon: BookOpen,     label: 'Ledger' },
                { id: 'cashflow', icon: BarChart3,   label: 'Cash flow' },
                { id: 'orders',  icon: ShoppingCart, label: 'Purchase Orders / Bills' },
                { id: 'pricing', icon: Tag,          label: 'Price History' },
              ].map(t => {
                const Icon = t.icon;
                const active = drawerTab === t.id;
                return (
                  <button key={t.id}
                    onClick={() => setDrawerTab(t.id)}
                    data-testid={`creditor-drawer-tab-${t.id}`}
                    className={`flex items-center gap-1.5 px-3 py-2 text-[11px] rounded-t-lg transition-colors border-b-2 whitespace-nowrap ${
                      active
                        ? 'border-amber-500 text-amber-300 bg-zinc-900'
                        : 'border-transparent text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60'
                    }`}>
                    <Icon size={12} /> {t.label}
                  </button>
                );
              })}
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto" data-testid={`creditor-drawer-tab-content-${drawerTab}`}>
              {drawerTab === 'ledger' && (
                <>
                  {/* iter210 — Role filter pills (only for "Both" clients) */}
                  {ledgerClient?.type === 'Both' && (
                    <div className="flex items-center gap-2 px-3 sm:px-4 py-2 border-b border-zinc-800/60 bg-zinc-900/40 sticky top-0 z-10 overflow-x-auto" data-testid="creditor-role-filter-bar">
                      <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-1">View:</span>
                      {[
                        { id: 'all', label: 'All', count: (ledger?.entries || []).length },
                        { id: 'debtor', label: 'As Debtor (Sales)', count: ledger?.role_split?.debtor?.entries_count || 0 },
                        { id: 'creditor', label: 'As Creditor (Purchases)', count: ledger?.role_split?.creditor?.entries_count || 0 },
                      ].map(p => (
                        <button key={p.id} data-testid={`creditor-role-filter-${p.id}`} onClick={() => setCreditorRoleFilter(p.id)}
                          className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors whitespace-nowrap ${creditorRoleFilter === p.id
                            ? 'bg-amber-500/20 border-amber-500/60 text-amber-300'
                            : 'border-zinc-700 text-zinc-400 hover:text-zinc-200'}`}>
                          {p.label} <span className="opacity-60">·{p.count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <LedgerFilters clientId={ledgerClient.id} clientName={ledgerClient.name} entries={ledger?.entries || []} filters={ledgerFilters} setFilters={setLedgerFilters}
                    role={ledgerClient?.type === 'Both' && creditorRoleFilter !== 'all' ? creditorRoleFilter : ''} />
                  <LedgerTable
                    loading={ledgerLoading}
                    entries={applyLedgerFilters((ledger?.entries || []).filter(e => ledgerClient?.type !== 'Both' || creditorRoleFilter === 'all' || e.role === creditorRoleFilter), ledgerFilters)}
                    role={creditorRoleFilter === 'debtor' ? 'debtor' : 'creditor'}
                    clientId={ledgerClient.id}
                    clientName={ledgerClient.name}
                    canEdit={canEditLedger}
                    onRefresh={() => viewLedger(ledgerClient)}
                  />
                </>
              )}

              {drawerTab === 'cashflow' && (
                <MonthlyCashflowTable clientId={ledgerClient.id} compact side={ledgerClient?.type === 'Both' && creditorRoleFilter === 'debtor' ? 'debtor' : 'creditor'} />
              )}

              {drawerTab === 'orders' && (
                !vendorTxns.loaded ? (
                  <div className="flex items-center justify-center h-32"><RefreshCw size={18} className="animate-spin text-zinc-500" /></div>
                ) : (vendorTxns.orders.length === 0 && vendorTxns.bills.length === 0) ? (
                  <div className="text-center text-zinc-500 text-sm py-10">No purchase orders or bills found for this vendor</div>
                ) : (
                  <div className="p-4 space-y-4">
                    {/* Purchase Orders */}
                    <div data-testid="vendor-pos-section">
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">
                        Purchase Orders ({vendorTxns.orders.length})
                      </div>
                      {vendorTxns.orders.length === 0 ? (
                        <div className="text-zinc-600 text-xs italic">No POs raised to this vendor.</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-zinc-900">
                              <tr className="text-zinc-500 text-[10px] uppercase tracking-wider">
                                <th className="text-left px-3 py-2 font-medium">Date</th>
                                <th className="text-left px-3 py-2 font-medium">PO #</th>
                                <th className="text-left px-3 py-2 font-medium">Items</th>
                                <th className="text-right px-3 py-2 font-medium">Amount</th>
                                <th className="text-left px-3 py-2 font-medium">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {vendorTxns.orders.map(o => (
                                <tr key={o.id} className="border-b border-zinc-800/40 hover:bg-zinc-800/20" data-testid={`vendor-po-row-${o.id}`}>
                                  <td className="px-3 py-2 text-zinc-500 font-mono">{(o.order_date || o.created_at || '').slice(0, 10)}</td>
                                  <td className="px-3 py-2 font-mono text-sky-300 text-[10px]">{o.id?.slice(0, 12)}</td>
                                  <td className="px-3 py-2 text-zinc-300 text-[11px]">
                                    {(o.items || []).slice(0, 2).map(it => it.material_name).join(', ') || '—'}
                                    {(o.items || []).length > 2 && <span className="text-zinc-500"> +{o.items.length - 2}</span>}
                                  </td>
                                  <td className="px-3 py-2 text-right font-mono text-zinc-100">{fmt(o.total_amount)}</td>
                                  <td className="px-3 py-2"><span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300">{o.status || 'Open'}</span></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                    {/* Purchase Bills */}
                    <div data-testid="vendor-bills-section">
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">
                        Purchase Bills ({vendorTxns.bills.length})
                      </div>
                      {vendorTxns.bills.length === 0 ? (
                        <div className="text-zinc-600 text-xs italic">No bills received yet.</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-zinc-900">
                              <tr className="text-zinc-500 text-[10px] uppercase tracking-wider">
                                <th className="text-left px-3 py-2 font-medium">Date</th>
                                <th className="text-left px-3 py-2 font-medium">Invoice</th>
                                <th className="text-left px-3 py-2 font-medium">Item / Lot</th>
                                <th className="text-right px-3 py-2 font-medium">Amount</th>
                                <th className="text-right px-3 py-2 font-medium">Paid</th>
                                <th className="text-left px-3 py-2 font-medium">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {vendorTxns.bills.map(b => {
                                const paid = parseFloat(b.paid_amount || 0);
                                const total = parseFloat(b.total_amount || 0);
                                const outstanding = total - paid;
                                return (
                                <tr key={b.id} className="border-b border-zinc-800/40 hover:bg-zinc-800/20" data-testid={`vendor-bill-row-${b.id}`}>
                                  <td className="px-3 py-2 text-zinc-500 font-mono">{(b.invoice_date || b.created_at || '').slice(0, 10)}</td>
                                  <td className="px-3 py-2 font-mono text-amber-300 text-[10px]">{b.invoice_number || b.id?.slice(0, 10)}</td>
                                  <td className="px-3 py-2 text-zinc-300 text-[11px]">
                                    {b.product_name || ((b.items || [])[0]?.material_name) || '—'}
                                    {(b.items || []).length > 1 && <span className="text-zinc-500"> +{b.items.length - 1}</span>}
                                    {(b.lot_no || (b.items || [])[0]?.lot_no) && (
                                      <span className="ml-1 text-[9px] font-mono bg-amber-900/30 text-amber-300 border border-amber-700/40 px-1 rounded">
                                        {b.lot_no || b.items[0].lot_no}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-right font-mono text-zinc-100">{fmt(total)}</td>
                                  <td className="px-3 py-2 text-right font-mono">
                                    <span className="text-green-400">{fmt(paid)}</span>
                                    {outstanding > 0.5 && (
                                      <div className="text-[9px] text-red-400 font-medium">Due {fmt(outstanding)}</div>
                                    )}
                                  </td>
                                  <td className="px-3 py-2">
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                      (b.payment_status || '').toLowerCase() === 'paid' ? 'bg-green-900/40 text-green-300' :
                                      (b.payment_status || '').toLowerCase() === 'partial' ? 'bg-amber-900/40 text-amber-300' :
                                      'bg-red-900/30 text-red-300'
                                    }`}>{b.payment_status || 'Unpaid'}</span>
                                  </td>
                                </tr>
                              );})}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )
              )}

              {drawerTab === 'pricing' && (
                priceHistory.loading ? (
                  <div className="flex items-center justify-center h-32"><RefreshCw size={18} className="animate-spin text-zinc-500" /></div>
                ) : !priceHistory.data ? (
                  <div className="text-center text-zinc-500 text-sm py-10">Loading…</div>
                ) : priceHistory.data.materials.length === 0 ? (
                  <div className="text-center text-zinc-500 text-sm py-10">No price history — no items purchased from this vendor yet.</div>
                ) : (
                  <div className="p-4 space-y-3" data-testid="vendor-price-history">
                    {priceHistory.data.materials.map(m => (
                      <details key={m.material_name} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid={`vendor-mat-${m.material_name.replace(/\s+/g, '-')}`}>
                        <summary className="px-3 py-2.5 cursor-pointer hover:bg-zinc-800/50 flex items-center gap-3 list-none">
                          <span className="text-zinc-100 font-medium text-[13px] flex-1 truncate">{m.material_name}</span>
                          <span className="text-[10px] text-zinc-500">{m.purchases_count}x</span>
                          <div className="text-right">
                            <div className="font-mono text-amber-300 text-sm">{fmt2(m.latest_rate)}</div>
                            <div className="text-[9px] text-zinc-500">latest / {m.unit}</div>
                          </div>
                          <PricePill delta={m.total_overall_delta_pct} />
                        </summary>
                        <div className="border-t border-zinc-800">
                          {/* Compact summary row */}
                          <div className="grid grid-cols-4 gap-2 px-3 py-2 bg-zinc-950/50 text-[10px] border-b border-zinc-800">
                            <div><span className="text-zinc-500">First</span> <span className="font-mono text-zinc-300">{fmt2(m.first_rate)}</span></div>
                            <div><span className="text-zinc-500">Min</span>   <span className="font-mono text-emerald-300">{fmt2(m.min_rate)}</span></div>
                            <div><span className="text-zinc-500">Max</span>   <span className="font-mono text-red-300">{fmt2(m.max_rate)}</span></div>
                            <div><span className="text-zinc-500">Avg</span>   <span className="font-mono text-zinc-300">{fmt2(m.avg_rate)}</span></div>
                          </div>
                          <div className="overflow-x-auto max-w-full">
                          <table className="w-full text-[11px] min-w-[480px]">
                            <thead className="bg-zinc-950/30">
                              <tr className="text-[10px] text-zinc-500">
                                <th className="text-left px-3 py-1.5 font-medium">Date</th>
                                <th className="text-left px-3 py-1.5 font-medium">Invoice</th>
                                <th className="text-right px-3 py-1.5 font-medium">Rate</th>
                                <th className="text-right px-3 py-1.5 font-medium">Qty</th>
                                <th className="text-left px-3 py-1.5 font-medium">Lot</th>
                                <th className="text-right px-3 py-1.5 font-medium">Δ vs prev</th>
                              </tr>
                            </thead>
                            <tbody>
                              {m.points.map((pt, i) => (
                                <tr key={i} className="border-t border-zinc-800/40">
                                  <td className="px-3 py-1.5 font-mono text-zinc-500">{pt.date}</td>
                                  <td className="px-3 py-1.5 font-mono text-amber-300/80 text-[10px]">{pt.invoice}</td>
                                  <td className="px-3 py-1.5 text-right font-mono text-zinc-100">{fmt2(pt.rate)}</td>
                                  <td className="px-3 py-1.5 text-right font-mono text-zinc-400">{pt.quantity}</td>
                                  <td className="px-3 py-1.5">
                                    {pt.lot_no ? (
                                      <span className="text-[9px] font-mono bg-amber-900/30 text-amber-300 border border-amber-700/40 px-1.5 py-0.5 rounded">{pt.lot_no}</span>
                                    ) : <span className="text-zinc-700">—</span>}
                                  </td>
                                  <td className="px-3 py-1.5 text-right"><PricePill delta={pt.delta_pct} abs={pt.delta_abs} /></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                        </div>
                      </details>
                    ))}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}
      <ConfirmDeleteModal open={deleteState.open} onClose={cancelDelete} onConfirm={confirmDelete} title={deleteState.title} message={deleteState.message} />

      {ledgerClient && (
        <SettlementEditor
          open={showLumpsum}
          clientId={ledgerClient.id}
          clientName={ledgerClient.name}
          side="creditor"
          onClose={() => setShowLumpsum(false)}
          onSaved={() => viewLedger(ledgerClient)}
        />
      )}
    </div>
  );
}

// iter196 — small pill that shows price-change direction.
function PricePill({ delta, abs }) {
  if (delta == null || delta === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] text-zinc-500 font-mono">
        <Minus size={9} /> 0%
      </span>
    );
  }
  const up = delta > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-mono px-1.5 py-0.5 rounded ${
      up ? 'bg-red-900/30 text-red-300 border border-red-700/40' : 'bg-emerald-900/30 text-emerald-300 border border-emerald-700/40'
    }`}>
      {up ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
      {delta > 0 ? '+' : ''}{delta}%
      {abs != null && abs !== 0 && (
        <span className="opacity-70 ml-0.5">({abs > 0 ? '+' : ''}₹{abs})</span>
      )}
    </span>
  );
}
